// src/components/Layout/navConfig.ts — the navigation tree, once.
//
// There used to be two of these. The desktop sidebar held the real tree and
// MobileBottomNav.tsx held a hand-kept copy, and a copy of a tree that changes
// every week is a copy that is wrong every week. By the time it was measured
// the phone was 35 destinations behind the desktop — AI Agents, Leads &
// Bookings, Voice Campaigns, Agent Console, IVR States, Reply Rules, Contact
// Ceiling and the whole Platform zone were simply absent on a phone — while
// still offering two pages (/agents/create, /telephony/campaigns/create) that
// the desktop nav had long since stopped listing. It also still grouped by
// channel (Telephony / WhatsApp / SMS / RCS), which is the arrangement the
// desktop nav was deliberately moved AWAY from when it was split into zones.
//
// None of that could fail a build or a test: a second literal array is valid
// TypeScript no matter how stale it is. So the array is gone, and the phone,
// the tablet and the desktop now read the same one.
//
// What is shared is the DATA and the GATING — paths, labels, icons, order,
// role lists, feature and portal locks. Presentation is not: the sidebar draws
// a rail that collapses to icons, the drawer draws a sheet, the bottom bar
// draws five tabs. Each renders this the way its own breakpoint needs.
//
// Note there is no tablet nav. Layout.tsx switches on `lg` alone, so anything
// under 1024px — every tablet in portrait included — gets the mobile drawer.
// That is one fewer copy to keep in step, and it is why the drift above was
// not just a phone problem.
import {
  Armchair,
  Phone,
  Activity,
  MessagesSquare,
  Bot,
  Brain,
  Server,
  Gauge,
  Workflow,
  PhoneForwarded,
  Shield,
  Building2,
  FileText,
  MessageSquare,
  Settings,
  FileCheck,
  Megaphone,
  PhoneCall,
  Mail,
  MessageCircle,
  Radio,
  Smartphone,
  Users,
  ClipboardList,
  Clock,
  Zap,
  Share2,
  Plug,
  Database,
  DollarSign,
  Crown,
  ShieldOff,
  UserCog,
  ScrollText,
  CreditCard,
  Receipt,
  Wallet,
  Code,
  Key,
  Webhook,
  Reply,
  Book,
  BarChart3,
  HelpCircle,
  Palette,
  Play,
  SlidersHorizontal,
  ShieldAlert,
  Headphones,
  Hash,
  PhoneOutgoing,
  CalendarClock,
  PhoneIncoming,
  Inbox,
  AtSign,
  Landmark,
  ShieldCheck,
  CalendarCheck,
  KeyRound,
  PhoneOff,
  Tags,
} from 'lucide-react';
import { canSeeItem, canSeeSection, NavPrincipal } from './navVisibility';

export type Zone = 'workspace' | 'administration' | 'platform';

export interface MenuItem {
  path: string;
  zone?: Zone;
  icon: any;
  label: string;
  roles: string[];
  badge?: string;
  // Optional sub-group label. Items sharing a group render under a small
  // header inside the section (used to de-clutter the large Telephony menu).
  group?: string;
}

export interface MenuSection {
  id: string;
  zone: Zone;
  label: string;
  icon: any;
  roles: string[];
  items: MenuItem[];
  comingSoon?: boolean;
}

// ── Roles ─────────────────────────────────────────────────────────────────
// Named groups rather than repeating literal arrays on every item — the old
// shape restated all six role names ~50 times, which made it impossible to
// see at a glance who could reach what.
const EVERYONE = ['master_admin', 'super_admin', 'service_admin', 'service_user', 'operator_admin', 'operator_user'];
const MANAGERS = ['master_admin', 'super_admin', 'service_admin', 'operator_admin'];
const ADMINS = ['master_admin', 'super_admin', 'service_admin'];
const SUPERS = ['master_admin', 'super_admin'];
const INFRA = ['master_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin'];
// API keys and webhooks are credentials, not everyday features — admins only.
// 'developer' is listed ahead of the backend issuing it: ROLES currently has
// six roles and none of them is developer, so that entry is inert until a
// developer profile actually exists. It is here so that shipping the role
// server-side is all that's needed, with no frontend change.
const DEVELOPERS = ['master_admin', 'super_admin', 'service_admin', 'operator_admin', 'developer'];
// Suspending an organization is platform-level. Deliberately NOT SUPERS:
// super_admin is granted per service organization (e.g. XtremeGenAI has its
// own), so including it would let a provider's admin lift the suspension
// placed on that provider.
const MASTER_ONLY = ['master_admin'];
// Platform billing (GG's receivables, every credit account, deferred revenue): the roles
// the billing backend treats as platform-wide (isMasterAdmin || isPlatformAdmin).
const PLATFORM_BILLING = ['master_admin', 'platform_admin'];
// /v1/crm/custom-api/deliveries: platform and provider admins, as the endpoint lists them.
const CUSTOM_API_DELIVERIES = ['master_admin', 'platform_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin'];
// /billing/my/** (MyBillingController): every billing admin role, both spellings.
const MY_BILLING_ROLES = [
  'master_admin', 'platform_admin', 'super_admin', 'provider_owner', 'service_admin', 'provider_admin',
  'operator_admin', 'business_admin', 'business_owner',
];
// Live Calls: every administrator, provider or business. The server scopes the
// list to the acting organisation's tree (the platform sees everything) — see
// SupervisionController / LiveCallAccess. Users never get it.
const LIVE_CALLS = [
  'master_admin', 'platform_admin', 'super_admin', 'provider_owner', 'service_admin', 'provider_admin',
  'operator_admin', 'business_admin',
];

// TRAI compliance pages: every member reads; writes are role-gated inside (both spellings).
const TRAI_COMPLIANCE = [
  ...EVERYONE, 'platform_admin', 'provider_owner', 'provider_admin', 'provider_user',
  'business_owner', 'business_admin', 'business_user', 'org_admin',
];

/**
 * The navigation tree. One definition, read by every breakpoint.
 *
 * <p>It is also what the portal-profile editor builds its tab picker from,
 * rather than a hand-maintained copy: the allowlist is a list of paths, and a
 * hand-typed path that matches nothing locks a tab for a reason nobody can find
 * afterwards. Typing paths by hand is how you end up allowing "/dashboard",
 * which this portal does not have — Overview is "/".
 *
 * <p>Pure config: icons, labels, paths and role constants, closing over
 * nothing. That is what lets a component, a test and a page all read it.
 */
export const NAV_SECTIONS: MenuSection[] = [
  // ── Workspace ───────────────────────────────────────────────────────────
  {
    // Channel-agnostic: an agent binds to VOICE / CHAT / WHATSAPP / RCS / EMAIL
    // via AgentChannel, so it is one destination rather than one per channel.
    id: 'agents',
    label: 'AI Agents',
    icon: Bot,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      { path: '/agents', icon: Bot, label: 'All Agents', roles: EVERYONE },
      { path: '/agents/memory-profiles', icon: Brain, label: 'Memory Profiles', roles: MANAGERS },
      // Where an org's agents run (voicex) + credentials — admin-gated.
      { path: '/agents/voice-ai-integrations', icon: Plug, label: 'Voice AI Integrations', roles: MASTER_ONLY },
      // Where an org's appointments and leads actually live — Zoho, Zenoti,
      // LeadSquared, n8n, or the built-in store.
      { path: '/crm/integrations', icon: Plug, label: 'Booking & CRM', roles: DEVELOPERS },
      { path: '/crm/booking-setup', icon: CalendarClock, label: 'Booking Setup', roles: MANAGERS },
      // What the agents brought in, as opposed to Call Activity below, which
      // is what the pipeline then did with it. Same audience as Contacts.
      { path: '/crm/captures', icon: Inbox, label: 'Leads & Bookings', roles: EVERYONE },
      // The shape of the same thing: how many leads, by which agent, and where
      // each one stopped. Sits next to the list rather than under Analytics
      // because it answers a question about THESE rows, and somebody who
      // wants it is already looking at them.
      { path: '/crm/lead-overview', icon: Gauge, label: 'Lead Overview', roles: EVERYONE },
      { path: '/crm/call-activity', icon: PhoneIncoming, label: 'Call Activity', roles: MANAGERS },
      // Custom API pushes, call by call — CustomApiDeliveryController's own role list.
      { path: '/crm/custom-api-deliveries', icon: Webhook, label: 'Custom API Deliveries', roles: CUSTOM_API_DELIVERIES },
    ],
  },
  {
    // Every record of a conversation, whatever carried it. Previously split
    // across Telephony/Monitor, WhatsApp, SMS and RCS.
    id: 'conversations',
    label: 'Conversations',
    icon: MessageCircle,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      { path: '/calls', icon: Phone, label: 'Total Calls', group: 'Voice', roles: EVERYONE },
      { path: '/sessions', icon: Bot, label: 'AI Calls', group: 'Voice', roles: EVERYONE },
      // MOVED OUT OF Contact Center, whose section gate is SUPERS. That gate hid
      // this from every business admin — including the AI-only tenants it is most
      // useful to, who run outbound agents with no seats and therefore have no
      // reason to be in a section built around softphones.
      //
      // Only this ONE entry moved. Widening the Contact Center gate would newly
      // expose Agent Console, Softphone, Seats and the Team Directory to ~40
      // tenants, which is a separate decision.
      { path: '/telephony/outbound', icon: PhoneOutgoing, label: 'Place a Call', group: 'Voice', roles: EVERYONE },
      { path: '/transfers', icon: PhoneForwarded, label: 'Transferred Calls', group: 'Voice', roles: EVERYONE },
      { path: '/call-failures', icon: ShieldAlert, label: 'Call Failures', group: 'Voice', roles: EVERYONE },
      // Recordings is hidden: not useful in its current state. The route in
      // App.tsx is deliberately left in place so an existing bookmark still
      // resolves rather than bouncing to the dashboard — restore this line to
      // bring it back.
      { path: '/whatsapp/chats', icon: MessageCircle, label: 'WhatsApp Chats', group: 'Messaging', roles: EVERYONE },
      { path: '/whatsapp/message-logs', icon: ClipboardList, label: 'WhatsApp Logs', group: 'Messaging', roles: MANAGERS },
      { path: '/sms/messages', icon: Mail, label: 'SMS Messages', group: 'Messaging', roles: EVERYONE },
      { path: '/rcs/messages', icon: Smartphone, label: 'RCS Logs', group: 'Messaging', roles: MANAGERS },
      { path: '/whatsapp/calls', icon: PhoneCall, label: 'WhatsApp Calls', group: 'Messaging', roles: EVERYONE, badge: 'Soon' },
    ],
  },
  {
    // One Campaigns destination. This concept previously existed five times.
    id: 'campaigns',
    label: 'Campaigns',
    icon: Megaphone,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      // One Voice Campaigns entry — AI Agent / OBD / Standard are tabs within it.
      { path: '/telephony/campaigns', icon: Megaphone, label: 'Voice Campaigns', group: 'Voice', roles: MANAGERS },
      { path: '/whatsapp/campaigns', icon: MessageSquare, label: 'WhatsApp Campaigns', group: 'Messaging', roles: EVERYONE },
      { path: '/sms/campaigns', icon: Mail, label: 'SMS Campaigns', group: 'Messaging', roles: EVERYONE },
      { path: '/rcs/campaigns', icon: Smartphone, label: 'RCS Campaigns', group: 'Messaging', roles: EVERYONE },
        { path: '/email/campaigns', icon: AtSign, label: 'Email Campaigns', group: 'Messaging', roles: EVERYONE },
      // Multi-step journeys (call → wait → WhatsApp → CRM…). Same roles as Voice Campaigns, since a
      // workflow places calls. Hidden unless the build names `workflows` in REACT_APP_ENABLE_FEATURES.
      { path: '/workflows', icon: Workflow, label: 'Workflows', group: 'Automation', roles: MANAGERS },
    ],
  },
  {
    id: 'contacts',
    label: 'Contacts',
    icon: Users,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      { path: '/user-directory', icon: Activity, label: 'Overview', roles: EVERYONE },
      { path: '/user-directory/contacts', icon: Users, label: 'All Contacts', roles: EVERYONE },
      { path: '/user-directory/vip-management', icon: Crown, label: 'VIP Lists', roles: EVERYONE },
      { path: '/user-directory/dnd-management', icon: ShieldOff, label: 'DND Lists', roles: EVERYONE },
    ],
  },
  {
    // Split out of the old "Ops Tools", which was misnamed: only Live Calls
    // is an ops tool. A seat, a softphone, the console an agent works in and
    // the org's own extensions and ring groups belong to whoever owns the
    // organization, business or service — so they sit in Workspace, and a
    // BUSINESS tenant keeps them.
    //
    // The section gate stays SUPERS, exactly as it was under the old label,
    // so this move changes the grouping and nothing about who sees what.
    // That gate is stricter than the items it contains — several are EVERYONE
    // and are therefore invisible to the agents they were written for — but
    // widening it would newly expose these screens to ~40 tenants, which is a
    // different decision and belongs in its own change.
    id: 'contact_center',
    label: 'Contact Center',
    icon: Headphones,
    zone: 'workspace',
    roles: SUPERS,
    items: [
      // No role gate beyond the section: for someone taking calls this is the
      // whole product.
      { path: '/telephony/console', icon: Headphones, label: 'Agent Console', roles: EVERYONE },
      // EVERYONE: this is the phone an agent takes calls on, not just a
      // trunk-test tool. The credential endpoint provisions only for the
      // calling principal and into that caller's own organization context.
      // Hidden unless the org has PURCHASED seats (or master / Green Giraffe):
      // navVisibility.softphoneAllows, fed by /telephony/softphone/entitlement.
      { path: '/telephony/softphone', icon: PhoneCall, label: 'Softphone', roles: EVERYONE },
      // The directory is org-scoped server-side and exposes only name +
      // extension, so any member may read it. Gating it to admins would hide
      // colleagues from the people who need to call them.
      { path: '/telephony/directory', icon: Users, label: 'Team Directory', roles: EVERYONE },
      // ADMINS: seats are self-service per organization, so an admin manages
      // their own without going through the platform.
      { path: '/telephony/extensions', icon: Hash, label: 'Extensions', roles: ADMINS },
      // Same audience as Extensions: a group is built from an organization's own
      // seats and has no cross-tenant consequence, so it is self-service too.
      { path: '/telephony/groups', icon: Users, label: 'Ring Groups', roles: ADMINS },
    ],
  },
  {
    // Rules that run without a human: call routing, hours, escalation, lead
    // assignment. Previously buried inside Telephony > Numbers & IVR.
    id: 'automations',
    label: 'Automations',
    icon: Zap,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      { path: '/telephony/ivr', icon: Zap, label: 'IVR Apps', roles: EVERYONE },
      { path: '/telephony/ivr/states', icon: Workflow, label: 'IVR States', roles: MANAGERS },
      // Master: import/drift/push audit. Others: "My IVR apps" — the flows filed to them, editable
      // (draft → validate → preview → publish → rollback) when every number using one is theirs.
      { path: '/telephony/ivr/switch-sync', icon: Workflow, label: 'IVR Apps · Publish', roles: MANAGERS },
      { path: '/telephony/business-hours', icon: Clock, label: 'Business Hours', roles: EVERYONE },
      { path: '/telephony/transfer-profiles', icon: PhoneForwarded, label: 'Transfer Profiles', roles: MANAGERS },
      { path: '/whatsapp/lead-assignment', icon: Users, label: 'Lead Routing', roles: MANAGERS },
      { path: '/whatsapp/reply-rules', icon: Zap, label: 'Reply Rules', roles: MANAGERS },
      // Next to Reply Rules on purpose: switching the agent on is what STOPS
      // those rules acting on the chats it holds, and the two settings being
      // adjacent is the cheapest way to make that relationship visible.
      { path: '/whatsapp/ai-agent', icon: Bot, label: 'WhatsApp AI Agent', roles: MANAGERS },
      // Read-only: it explains what an instruction would do and sends nothing,
      // so it is safe for everybody who may build a campaign — the same people
      // the plan endpoint admits.
      { path: '/ask', icon: MessagesSquare, label: 'Ask', roles: EVERYONE },
    ],
  },
  {
    // Per-channel connection and content. Setup existed 3x; it is config, not
    // day-to-day work, so it lives in one place away from the daily surfaces.
    id: 'channels',
    label: 'Channel Setup',
    icon: Share2,
    zone: 'workspace',
    roles: MANAGERS,
    items: [
      { path: '/whatsapp', icon: Activity, label: 'WhatsApp Overview', group: 'WhatsApp', roles: EVERYONE },
      { path: '/whatsapp/setup', icon: Settings, label: 'WhatsApp Setup', group: 'WhatsApp', roles: MANAGERS },
      { path: '/whatsapp/templates', icon: FileCheck, label: 'WhatsApp Templates', group: 'WhatsApp', roles: EVERYONE },
      { path: '/phones', icon: Phone, label: 'Phone Numbers', group: 'Voice', roles: EVERYONE },
      // Holds credentials for external switch databases — same gate as Developer.
      { path: '/telephony/data-sources', icon: Database, label: 'Call Data Sources', group: 'Voice', roles: DEVELOPERS },
      { path: '/sms', icon: Activity, label: 'SMS Overview', group: 'SMS & RCS', roles: EVERYONE },
      { path: '/sms/setup', icon: Settings, label: 'SMS Setup', group: 'SMS & RCS', roles: MANAGERS },
      { path: '/rcs', icon: Activity, label: 'RCS Overview', group: 'SMS & RCS', roles: EVERYONE },
      { path: '/rcs/setup', icon: Settings, label: 'RCS Setup', group: 'SMS & RCS', roles: MANAGERS },
      { path: '/rcs/rich-cards', icon: FileCheck, label: 'RCS Rich Cards', group: 'SMS & RCS', roles: EVERYONE },
      { path: '/email/settings', icon: Settings, label: 'Email Setup', group: 'Email', roles: MANAGERS },
      { path: '/email/templates', icon: FileCheck, label: 'Email Templates', group: 'Email', roles: EVERYONE },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: BarChart3,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      // 'Soon': the route renders ComingSoon for every role (2026-10-14 audit) — greyed, not a dead link.
      { path: '/analytics', icon: BarChart3, label: 'Analytics', roles: EVERYONE, badge: 'Soon' },
      { path: '/reports', icon: FileText, label: 'Reports', roles: EVERYONE },
      { path: '/telephony', icon: Activity, label: 'Telephony Overview', roles: EVERYONE },
    ],
  },
  {
    id: 'developer',
    // EVERYONE at the section level, DEVELOPERS on each item that needs it.
    // canSeeSection and canSeeItem are checked independently, so this opens the
    // heading without opening API Keys: a business user sees this section
    // containing only Webhook Stubs, which is a self-service feature every
    // tenant is meant to have. Gating the section at DEVELOPERS hid the stubs
    // from exactly the people who queue their own integrations.
    label: 'Developer',
    icon: Code,
    zone: 'workspace',
    roles: EVERYONE,
    items: [
      { path: '/developer/api-keys', icon: Key, label: 'API Keys', roles: DEVELOPERS },
      { path: '/developer/webhooks', icon: Webhook, label: 'Webhooks', roles: DEVELOPERS },
      // The inverse of the line above: those are webhooks cpass CALLS, these are
      // URLs cpass ANSWERS. Adjacent because somebody looking for one routinely
      // wants the other, and 'Webhooks' alone would be read as covering both.
      { path: '/developer/webhook-stubs', icon: Reply, label: 'Webhook Stubs', roles: EVERYONE },
      // Docs are reference material — harmless, and useful to anyone building.
      { path: '/developer/docs', icon: Book, label: 'API Documentation', roles: DEVELOPERS },
      // Fires real requests, so it stays tighter than the rest of the section.
      { path: '/developer/api-tester', icon: Play, label: 'API Tester', roles: [...ADMINS, 'platform_admin'] },
    ],
  },

  // ── Administration ──────────────────────────────────────────────────────
  {
    id: 'team',
    label: 'Team & Access',
    icon: UserCog,
    zone: 'administration',
    roles: MANAGERS,
    items: [
      { path: '/users', icon: Shield, label: 'Users', roles: MANAGERS },
      // MANAGERS, which includes operator_admin — a business's own admin. There is
      // no business_admin role; a business's people are operators, so gating this
      // to ADMINS would hand the decision to the provider and leave the business
      // unable to make it.
      //
      // A nav entry rather than a row inside Settings: a business could only
      // withdraw its provider's access if it already knew the setting existed.
      { path: '/settings/provider-access', icon: ShieldCheck, label: 'Provider Access', roles: MANAGERS },
      // MANAGERS, not ADMINS: the ceiling decides whether a business's own
      // campaigns reach people, so the business has to be able to see and set
      // it. There is no business_admin role — a business's people are operators.
      //
      // A nav entry rather than a row buried in Settings, for the reason above
      // it: the endpoints shipped live and unreachable, which is how the
      // do-not-contact list sat at 0 rows across 47 organizations.
      { path: '/settings/contact-ceiling', icon: ShieldOff, label: 'Contact Ceiling', roles: MANAGERS },
      // MANAGERS for the same reason as the two above: it decides what a
      // business's own staff can see of its own customers, so the business has
      // to be able to set it. Gating to ADMINS would hand that to the provider.
      //
      // A nav entry rather than a row inside Settings, again for the reason
      // stated above: the endpoints for this shipped before the screen did, and
      // a policy nobody can find is a policy nobody sets.
      { path: '/settings/data-privacy', icon: ShieldCheck, label: 'Data Privacy', roles: MANAGERS },
      // ADMINS, NOT MANAGERS — and the difference is deliberate. The endpoints are
      // hasAnyRole(MASTER_ADMIN, PLATFORM_ADMIN, SUPER_ADMIN, PROVIDER_OWNER,
      // SERVICE_ADMIN, PROVIDER_ADMIN), which excludes operator_admin; listing this
      // for MANAGERS would show a business's own admin a page whose every button
      // 403s, the same trap documented on Number Ownership below.
      //
      // Both role spellings are in that list because the enum renamed them and every
      // real user still holds the LEGACY name, with no role hierarchy to bridge them.
      //
      // Provider-level rather than business-level: the application registered here
      // puts a reseller's name on the consent screen, and a business inherits its
      // provider's buttons exactly as it inherits the portal brand.
      { path: '/settings/sign-in-providers', icon: KeyRound, label: 'Sign-in Providers', roles: ADMINS },
      // MANAGERS, like its neighbours: it decides what happens to a business's
      // own meetings, so the business sets it. A nav entry rather than a row
      // inside Settings for the reason stated above — the webhook shipped
      // before this screen did, and an integration nobody can find is one
      // nobody turns on.
      { path: '/settings/bookings', icon: CalendarCheck, label: 'Meeting Bookings', roles: MANAGERS },
      { path: '/businesses', icon: Building2, label: 'Businesses', roles: ADMINS },
    ],
  },
  {
    // TRAI TCCCPR compliance. Every path is behind REACT_APP_ENABLE_FEATURES=trai-compliance
    // (FLAGGED_PATHS), so a normal build shows neither the section nor its items. Readable by
    // every member — the server scopes each list to the acting organisation (the platform may
    // list all); the write controls inside are gated by role on the page and on the server.
    id: 'trai_compliance',
    label: 'Compliance (TRAI)',
    icon: ShieldAlert,
    zone: 'administration',
    roles: TRAI_COMPLIANCE,
    items: [
      { path: '/compliance/categories', icon: Tags, label: 'Call Category', roles: TRAI_COMPLIANCE },
      { path: '/compliance/numbers', icon: Hash, label: 'Number Declarations', roles: TRAI_COMPLIANCE },
      { path: '/compliance/consent', icon: FileCheck, label: 'Consent', roles: TRAI_COMPLIANCE },
      { path: '/compliance/complaints', icon: ShieldAlert, label: 'Complaints', roles: TRAI_COMPLIANCE },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: CreditCard,
    zone: 'administration',
    // + platform_admin: the server treats it as platform-wide, and ADMINS alone hid the
    // whole section (Test Numbers included) from it.
    roles: [...ADMINS, 'platform_admin'],
    items: [
      // PLATFORM_BILLING (2026-10-14 audit): the Overview is GG's receivables (/billing/stats
      // and the voice statement are platform-only server-side) and Credit Accounts lists
      // every account (/billing/accounts is platform-only). Shown to a provider, both were
      // empty pages whose every call 404'd. A provider's own figures are on My billing.
      { path: '/billing', icon: Activity, label: 'Overview', roles: PLATFORM_BILLING },
      { path: '/billing/accounts', icon: Wallet, label: 'Credit Accounts', roles: PLATFORM_BILLING },
      { path: '/billing/rates', icon: DollarSign, label: 'Service Rates', roles: [...ADMINS, 'platform_admin'] },
      { path: '/billing/invoices', icon: Receipt, label: 'Invoices', roles: [...ADMINS, 'platform_admin'] },
      { path: '/billing/payments', icon: CreditCard, label: 'Payments', roles: [...ADMINS, 'platform_admin'] },
      { path: '/billing/usage', icon: BarChart3, label: 'Usage', roles: [...ADMINS, 'platform_admin'] },
      // Platform only, as its route (billingReportRules.DEFERRED_REVENUE_ROLES).
      { path: '/billing/deferred-revenue', icon: Landmark, label: 'Deferred Revenue', roles: PLATFORM_BILLING },
      // Platform staff only (backend: PLATFORM_ADMIN, MASTER_ADMIN): the list spans every
      // tenant, and "Adjust past charges" moves money on any account.
      { path: '/billing/test-numbers', icon: PhoneOff, label: 'Test Numbers', roles: ['master_admin', 'platform_admin'] },
      // Merchant accounts at Razorpay/Stripe per issuing organisation (backend: MASTER_ADMIN).
      { path: '/billing/payment-gateways', icon: CreditCard, label: 'Payment Gateways', roles: ['master_admin'] },
    ],
  },

  // ── Platform ────────────────────────────────────────────────────────────
  {
    id: 'infrastructure',
    label: 'Infrastructure',
    icon: Server,
    zone: 'platform',
    roles: INFRA,
    items: [
      { path: '/telephony/overview', icon: Server, label: 'Trunk Overview', roles: INFRA },
      { path: '/telephony/providers', icon: Server, label: 'Providers', roles: INFRA },
      // INFRA since provider reassign (13 Oct 2026): provider roles get a PROVIDER view
      // of this page (ProviderNumberOwnership.tsx) whose actions are
      // /v1/numbers/provider/* — granted to exactly INFRA's roles, both spellings —
      // so nothing on it 403s. The master view (acquire/release, push, retire) is
      // unchanged and still master-only. Ownership sits here rather than under
      // Channel Setup because it is inventory — provider_numbers, next to the trunks
      // the numbers come off — whereas Phone Numbers over there is inbound routing
      // on a different table entirely.
      { path: '/platform/numbers', icon: Landmark, label: 'Number Ownership', roles: INFRA },
      { path: '/telephony/capacity-limits', icon: Gauge, label: 'Capacity Limits', roles: INFRA },
      { path: '/telephony/workers/config', icon: SlidersHorizontal, label: 'Worker Config', roles: INFRA },
      { path: '/system-health', icon: Activity, label: 'System Health', roles: MASTER_ONLY },
    ],
  },
  {
    id: 'platform_whatsapp',
    label: 'WhatsApp Platform',
    icon: MessageSquare,
    zone: 'platform',
    roles: ADMINS,
    items: [
      // Also behind the WHATSAPP_EMBEDDED_SIGNUP_CONFIG grant (navVisibility
      // GRANT_PATHS): master always, a provider admin only once granted.
      { path: '/whatsapp/embedded-signup-config', icon: Shield, label: 'Embedded Signup', roles: ADMINS },
      { path: '/whatsapp/waba-registrations', icon: CreditCard, label: 'WABA Registrations', roles: SUPERS },
    ],
  },
  {
    // What is left of "Ops Tools" after the tenant-owned screens moved to
    // Contact Center: Live Calls. It was the cross-tenant monitor and therefore
    // platform-only; since 14 Oct 2026 the server scopes it per tenant (a
    // business admin sees its own calls and those of its businesses, the
    // platform sees all), so it lives in Workspace and businesses keep it.
    id: 'ops_tools',
    label: 'Ops Tools',
    icon: Radio,
    zone: 'workspace',
    roles: LIVE_CALLS,
    items: [
      { path: '/telephony/live-calls', icon: Radio, label: 'Live Calls', roles: LIVE_CALLS },
    ],
  },
  {
    id: 'service_controls',
    label: 'Service Controls',
    icon: ShieldOff,
    zone: 'platform',
    roles: MASTER_ONLY,
    items: [
      { path: '/platform/service-controls', icon: ShieldOff, label: 'Disable Services', roles: MASTER_ONLY },
      // The inverse switch: features off for every provider until granted here
      // (organization_feature_grants). First one: Embedded Signup config.
      { path: '/platform/feature-access', icon: KeyRound, label: 'Feature Access', roles: MASTER_ONLY },
      // Platform section: writing one of these decides what a whole reseller's
      // customers can see, so it stays with us rather than with the reseller.
      { path: '/platform/portal-profiles', icon: Palette, label: 'Portal Profiles', roles: MASTER_ONLY },
      // Seats are what a customer buys; extensions and the softphone are gated
      // on them. Green Giraffe only (2026-10-14) — the server also lets admins
      // of the platform root org in; they can open /platform/seats directly.
      { path: '/platform/seats', icon: Armchair, label: 'Seats', roles: MASTER_ONLY },
    ],
  },
];

// ── Zones ─────────────────────────────────────────────────────────────────
// The nav is split by *what you are doing*, not by which channel a feature
// belongs to:
//
//   Workspace      day-to-day work — everyone who uses the product
//   Administration running the account — who can spend, who has access
//   Platform       running the platform itself — operators only
//
// Zones are ordered so a service_user sees only Workspace and never learns a
// Platform zone exists. Within Workspace, features are grouped by job, so a
// campaign is one destination regardless of whether it goes out over voice,
// WhatsApp, SMS or RCS — previously "Campaigns" appeared five times.
export const COMMON_ITEMS: MenuItem[] = [
  { path: '/', icon: Activity, label: 'Overview', zone: 'workspace', roles: EVERYONE },
  { path: '/audit-logs', icon: ScrollText, label: 'Audit Logs', zone: 'administration', roles: ADMINS },
  // 'Soon': ComingSoon page. Revenue today lives on My billing (provider) and Billing > Overview (platform).
  { path: '/revenue', icon: DollarSign, label: 'Revenue', zone: 'administration', roles: ADMINS, badge: 'Soon' },
  // The organisation's OWN bill (payable upstream, receivable from its businesses,
  // prepaid wallet). Outside the 'billing' section and the /billing prefix on purpose:
  // those are the provider's ledger of its tenants and are hidden from a business,
  // whereas a business admin must see this. Roles = backend /billing/my/** (both
  // spellings); the server decides per organisation what is in it.
  { path: '/my-billing', icon: Wallet, label: 'My billing', zone: 'administration', roles: MY_BILLING_ROLES },
];

/**
 * The two destinations that sit outside the zones entirely — they are not part
 * of any product area, they are the account's own. The sidebar pins them to its
 * footer and the drawer gives them their own group, but they are listed here so
 * that "everything the nav can reach" is answerable from one array.
 */
export const UTILITY_ITEMS: MenuItem[] = [
  { path: '/settings', icon: Settings, label: 'Settings', roles: EVERYONE },
  { path: '/support', icon: HelpCircle, label: 'Help & Support', roles: EVERYONE },
];

/** The zone order every nav renders in, privileged zones last. */
export const ZONES: { id: Zone; label: string }[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'administration', label: 'Administration' },
  { id: 'platform', label: 'Platform' },
];

// ── Building a nav for one person ─────────────────────────────────────────
// Every consumer goes through these, which is the point: a breakpoint cannot
// accidentally filter differently from another one, because none of them
// filters at all.

/** A section whose items have already been narrowed to this principal. */
export interface NavZone {
  id: Zone;
  label: string;
  items: MenuItem[];
  sections: MenuSection[];
}

/**
 * Is this path outside the reseller's allowance? Supplied by
 * usePortalProfile(); defaults to "nothing is locked" so tests and the
 * portal-profile editor can call these without a provider.
 */
export type PathLock = (path: string) => boolean;

const NO_LOCK: PathLock = () => false;

/**
 * A tab outside this reseller's allowance is shown LOCKED rather than hidden:
 * the customer is meant to see where things will be. Both navs already render
 * `badge` as a greyed, non-clickable pill, so locking reuses that rather than
 * adding a second disabled state — one of them would otherwise drift.
 *
 * An item's own badge wins: 'Soon' is a statement about the product, 'Locked'
 * about this portal, and the product-level one is truer.
 */
export function filterNavItems(
  items: MenuItem[],
  principal: NavPrincipal,
  isLocked: PathLock = NO_LOCK,
): MenuItem[] {
  return items
    .filter((item) => canSeeItem(item, principal))
    .map((item) => (item.badge || !isLocked(item.path) ? item : { ...item, badge: 'Locked' }));
}

/**
 * The zones this principal may see, each already narrowed to the sections and
 * items they may see, with empty zones and empty sections dropped — a
 * service_user never learns an Administration or Platform zone exists.
 */
export function buildNavZones(
  principal: NavPrincipal,
  isLocked: PathLock = NO_LOCK,
): NavZone[] {
  return ZONES.map((zone) => ({
    ...zone,
    items: filterNavItems(
      COMMON_ITEMS.filter((i) => i.zone === zone.id),
      principal,
      isLocked,
    ),
    sections: NAV_SECTIONS.filter((sec) => sec.zone === zone.id)
      .filter((sec) => canSeeSection(sec, principal))
      .map((sec) => ({ ...sec, items: filterNavItems(sec.items, principal, isLocked) }))
      .filter((sec) => sec.items.length > 0),
  })).filter((zone) => zone.items.length > 0 || zone.sections.length > 0);
}

/** Every destination the nav can reach, gating aside. */
export const ALL_NAV_PATHS: string[] = [
  ...COMMON_ITEMS.map((i) => i.path),
  ...NAV_SECTIONS.flatMap((sec) => sec.items.map((i) => i.path)),
  ...UTILITY_ITEMS.map((i) => i.path),
];

/**
 * Exactly one nav destination wins for a given URL.
 *
 * An earlier version compared only the first path segment, which was safe while
 * each section owned a channel prefix. It no longer is: /whatsapp/* items live
 * in Conversations, Campaigns, Automations and Channel Setup, so /whatsapp/chats
 * lit up all four headers at once.
 *
 * Instead: match on a real path boundary, and let the longest match win, so
 *   /whatsapp/campaigns/123     -> "WhatsApp Campaigns" (the detail route)
 *   /telephony/campaigns/create -> the create page, not "Voice Campaigns"
 */
export function activeNavPath(pathname: string): string | null {
  const matches = (p: string) =>
    p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/');
  return ALL_NAV_PATHS.filter(matches).sort((a, b) => b.length - a.length)[0] ?? null;
}

/**
 * The bottom tab bar's four fixed destinations, plus More.
 *
 * Only the path and a short label are decided here — the icon, the role list,
 * the feature gate and the portal lock all come from the entry itself, so a tab
 * can never outlive the nav entry it points at or be visible to someone the
 * sidebar hides it from. A short label IS presentation (there are ~60px to
 * write in), which is why it is the one thing overridden.
 *
 * '/whatsapp/chats' rather than '/whatsapp': the overview lives in Channel
 * Setup, whose section gate is MANAGERS, so an operator_user — the person most
 * likely to be holding a phone — would have lost the tab. Chats is theirs.
 */
/**
 * The label the nav itself uses for whatever page is open.
 *
 * Header.tsx used to carry its own map of six paths, everything else falling
 * through to "Dashboard" — so most of the portal was titled "Dashboard",
 * including every page added after that map was written. The nav already knows
 * the name of all 82 destinations; there is no reason for a second list to
 * disagree with it.
 *
 * Detail routes (/crm/captures/contact/<id>) resolve to their parent entry,
 * which is the right answer for a title: activeNavPath already picks the
 * longest match on a path boundary.
 */
export function navLabel(pathname: string): string | null {
  const path = activeNavPath(pathname);
  if (path === null) return null;
  const all = [
    ...COMMON_ITEMS,
    ...NAV_SECTIONS.flatMap((sec) => sec.items),
    ...UTILITY_ITEMS,
  ];
  return all.find((i) => i.path === path)?.label ?? null;
}

export const MOBILE_TABS: ReadonlyArray<{ path: string; label: string }> = [
  { path: '/', label: 'Home' },
  { path: '/calls', label: 'Calls' },
  { path: '/whatsapp/chats', label: 'WhatsApp' },
  // Not /analytics: that route renders <ComingSoon/>. The bar has four slots
  // and this is the only navigation most phone users ever touch, so none of
  // them goes to a placeholder. Leads is what someone away from a desk
  // actually opens the portal for.
  { path: '/crm/captures', label: 'Leads' },
];

/**
 * The tabs this principal actually gets, resolved against their own nav tree.
 * A tab whose entry they cannot see is dropped rather than shown dead, and so
 * is one that is badged — a 'Soon' or 'Locked' tab occupies a fifth of the bar
 * and goes nowhere.
 */
export function mobileTabItems(
  principal: NavPrincipal,
  isLocked: PathLock = NO_LOCK,
): MenuItem[] {
  const visible = new Map<string, MenuItem>(
    buildNavZones(principal, isLocked)
      .flatMap((zone) => [...zone.items, ...zone.sections.flatMap((sec) => sec.items)])
      .map((item) => [item.path, item]),
  );
  return MOBILE_TABS.flatMap((tab) => {
    const item = visible.get(tab.path);
    return item && !item.badge ? [{ ...item, label: tab.label }] : [];
  });
}
